import { useEffect } from 'react';
import Box from '@mui/material/Box';
import { Resizable } from 're-resizable';
import { toast } from 'react-hot-toast';
/**
 * ResizableSplitter - Standardized Material Design 3 Resizable Panel
 * 
 * Features:
 * - Always-visible 2px gray divider
 * - Prominent 4px blue handle at 70% opacity
 * - Smooth hover animations with glow effect
 * - Real-time drag resizing with live updates
 * - LocalStorage persistence
 * - Constraint-based resizing (customizable min/max)
 * - Magnetic snap points at 25%, 50%, 75%
 * - Double-click reset to default
 * - Keyboard shortcut (Ctrl+\ or Cmd+\) to reset
 * - WCAG 2.1 AA compliant
 */
export function ResizableSplitter({
  storageKey,
  defaultWidth = 50,
  minWidth = '300px',
  maxWidth = '80%',
  width,
  setWidth,
  children,
  enabled = true,
  handleClassName = 'resize-handle',
  minWidthPercent = 20,
  maxWidthPercent = 80,
  enableSnapPoints = true,
  snapThreshold = 3,
  enableDoubleClickReset = true,
  enableKeyboardShortcut = true
}) {
  // Snap points at common percentages
  const snapPoints = [25, 33.33, 50, 66.67, 75];
  const applySnapPoints = percentage => {
    if (!enableSnapPoints) return percentage;
    for (const snapPoint of snapPoints) {
      if (Math.abs(percentage - snapPoint) < snapThreshold) {
        return snapPoint;
      }
    }
    return percentage;
  };
  const handleResizeStop = (e, direction, ref, d) => {
    const container = ref.parentElement;
    if (container) {
      const containerWidth = container.offsetWidth;
      const newPixelWidth = ref.offsetWidth;
      let newPercentage = newPixelWidth / containerWidth * 100;

      // Apply snap points
      newPercentage = applySnapPoints(newPercentage);

      // Apply constraints
      const constrainedWidth = Math.min(maxWidthPercent, Math.max(minWidthPercent, newPercentage));

      // 🔍 DEBUG: Log resize stop data
      console.log('🔵 ========== RESIZE STOP DEBUG ==========');
      console.log('📍 Splitter ID:', storageKey);
      console.log('📏 Container Width:', containerWidth + 'px');
      console.log('📏 New Pixel Width:', newPixelWidth + 'px');
      console.log('📊 New Percentage:', newPercentage + '%');
      console.log('📊 Constrained Width:', constrainedWidth + '%');
      console.log('📦 Element Rect:', ref.getBoundingClientRect());
      console.log('📦 Container Rect:', container.getBoundingClientRect());

      // Log all resize handles
      const handles = ref.querySelectorAll('[data-debug]');
      console.log('🎯 Found', handles.length, 'debug elements:');
      handles.forEach((handle, index) => {
        const element = handle;
        const rect = element.getBoundingClientRect();
        const styles = window.getComputedStyle(element);
        console.log(`  ${index + 1}. [${element.getAttribute('data-debug')}]`, {
          id: element.id,
          rect: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height
          },
          computed: {
            position: styles.position,
            left: styles.left,
            right: styles.right,
            width: styles.width,
            backgroundColor: styles.backgroundColor,
            transform: styles.transform
          }
        });
      });
      console.log('🔵 ====================================\n');
      setWidth(constrainedWidth);
      localStorage.setItem(storageKey, constrainedWidth.toString());

      // Show toast if snapped
      if (enableSnapPoints && Math.abs(newPercentage - constrainedWidth) > 0.1) {
        const snappedPoint = snapPoints.find(point => Math.abs(constrainedWidth - point) < 0.5);
        if (snappedPoint) {
          toast.info(`Snapped to ${Math.round(snappedPoint)}%`);
        }
      }
    }
  };
  const handleResize = (e, direction, ref, d) => {
    // Real-time width update during resize
    const container = ref.parentElement;
    if (container) {
      const containerWidth = container.offsetWidth;
      const newPixelWidth = ref.offsetWidth;
      const newPercentage = newPixelWidth / containerWidth * 100;
      const constrainedWidth = Math.min(maxWidthPercent, Math.max(minWidthPercent, newPercentage));
      setWidth(constrainedWidth);
    }
  };
  const resetToDefault = () => {
    setWidth(defaultWidth);
    localStorage.setItem(storageKey, defaultWidth.toString());
    toast.success(`Splitter reset to ${defaultWidth}%`);
  };

  // Double-click handler
  useEffect(() => {
    if (!enableDoubleClickReset || !enabled) return;
    const handleDoubleClick = e => {
      const target = e.target;
      if (target.closest(`.${handleClassName}`)) {
        resetToDefault();
      }
    };
    document.addEventListener('dblclick', handleDoubleClick);
    return () => document.removeEventListener('dblclick', handleDoubleClick);
  }, [handleClassName, enableDoubleClickReset, enabled, defaultWidth]);

  // Keyboard shortcut handler
  useEffect(() => {
    if (!enableKeyboardShortcut || !enabled) return;
    const handleKeyPress = e => {
      // Ctrl+\\ or Cmd+\\: Reset splitter
      if ((e.ctrlKey || e.metaKey) && e.key === '\\\\') {
        e.preventDefault();
        resetToDefault();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [enableKeyboardShortcut, enabled, defaultWidth]);

  // 🔍 DEBUG: Log on mount
  useEffect(() => {
    console.log('🟢 ========== SPLITTER MOUNTED ==========');
    console.log('📍 Splitter ID:', storageKey);
    console.log('📏 Current Width:', width + '%');
    console.log('📏 Default Width:', defaultWidth + '%');
    console.log('⚙️  Enabled:', enabled);
    console.log('🎨 Handle Class:', handleClassName);
    console.log('🟢 ====================================\n');

    // Log all splitters on page after a brief delay to allow DOM to settle
    setTimeout(() => {
      console.log('🌐 ========== ALL SPLITTERS ON PAGE ==========');
      const allSplitters = document.querySelectorAll('[data-debug="splitter-handle-container"]');
      console.log('🎯 Total Splitters Found:', allSplitters.length);
      allSplitters.forEach((splitter, index) => {
        const element = splitter;
        const rect = element.getBoundingClientRect();
        console.log(`  ${index + 1}. Splitter:`, {
          id: element.id,
          rect: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height
          }
        });
      });
      const allDividers = document.querySelectorAll('[data-debug="splitter-divider"]');
      console.log('📏 Total Dividers Found:', allDividers.length);
      allDividers.forEach((divider, index) => {
        const element = divider;
        const rect = element.getBoundingClientRect();
        const styles = window.getComputedStyle(element);
        console.log(`  ${index + 1}. Divider:`, {
          id: element.id,
          rect: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height
          },
          styles: {
            left: styles.left,
            transform: styles.transform,
            backgroundColor: styles.backgroundColor
          }
        });
      });
      const allIndicators = document.querySelectorAll('[data-debug="splitter-indicator"]');
      console.log('🔵 Total Indicators Found:', allIndicators.length);
      allIndicators.forEach((indicator, index) => {
        const element = indicator;
        const rect = element.getBoundingClientRect();
        const styles = window.getComputedStyle(element);
        console.log(`  ${index + 1}. Indicator:`, {
          id: element.id,
          rect: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height
          },
          styles: {
            left: styles.left,
            transform: styles.transform,
            backgroundColor: styles.backgroundColor,
            opacity: styles.opacity
          }
        });
      });

      // 🔍 DEBUG: Check re-resizable library handles
      const reResizableHandles = document.querySelectorAll('.resize-handle-recordings');
      console.log('🔧 Total re-resizable Handles Found:', reResizableHandles.length);
      reResizableHandles.forEach((handle, index) => {
        const element = handle;
        const rect = element.getBoundingClientRect();
        const styles = window.getComputedStyle(element);
        console.log(`  ${index + 1}. re-resizable Handle:`, {
          className: element.className,
          rect: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height
          },
          styles: {
            background: styles.background,
            backgroundColor: styles.backgroundColor,
            border: styles.border,
            borderRight: styles.borderRight,
            borderLeft: styles.borderLeft,
            position: styles.position,
            right: styles.right,
            zIndex: styles.zIndex
          }
        });
      });
      console.log('🌐 ==========================================\n');
    }, 1000);
  }, []);
  return <Resizable size={{
    width: `${width}%`,
    height: '100%'
  }} onResizeStop={handleResizeStop} onResize={handleResize} minWidth={minWidth} maxWidth={maxWidth} enable={{
    right: enabled
  }} handleStyles={{
    right: {
      width: '16px',
      cursor: enabled ? 'col-resize' : 'default',
      background: 'transparent',
      border: 'none',
      // Remove all borders from re-resizable handle
      borderLeft: 'none',
      // Explicitly remove left border
      borderRight: 'none',
      // Explicitly remove right border
      boxShadow: 'none',
      // Remove any box-shadow
      right: '-8px',
      // Position centered on the right edge - 8px on each side
      zIndex: 100,
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }} handleClasses={{
    right: handleClassName
  }} handleComponent={{
    right: enabled ? <Box id={`splitter-handle-container-${storageKey}`} data-debug="splitter-handle-container" sx={{
      width: '16px',
      // Match the handle width
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'col-resize',
      position: 'relative',
      // Changed from absolute
      backgroundColor: 'transparent',
      transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 100,
      '&:hover': {
        backgroundColor: 'rgba(41, 50, 229, 0.05)'
      },
      '&:hover .splitter-indicator': {
        opacity: 1,
        height: '120px',
        width: '6px',
        boxShadow: '0 0 12px rgba(41, 50, 229, 0.4)'
      }
    }}>
            {/* Handle indicator - Primary color - Single line design */}
            <Box id={`splitter-indicator-${storageKey}`} data-debug="splitter-indicator" className="splitter-indicator" sx={{
        width: '3px',
        height: '80px',
        backgroundColor: '#2932E5',
        // Primary color from guidelines
        borderRadius: '1.5px',
        opacity: 0.8,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)'
      }} />
          </Box> : undefined
  }} style={{
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    minHeight: 0,
  }}>
      {children}
    </Resizable>;
}